import axios from 'axios';
import { 
  Memorandum, 
  MemorandumRequest, 
  MemorandumStatus,
  MemorandumType,
  MemorandumStats,
  ApiResponse 
} from '../types/memorandum';

// IMPORTANT: Use the deployed backend URL
const API_URL = process.env.REACT_APP_API_URL || 'https://connect-app-v1-0-5.onrender.com';
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://connect-app-v1-0-5.onrender.com';

console.log('🚀 API Base URL:', API_URL);

// Create axios instance with /api prefix
const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config: any) => {
    const url = config.url || '';
    
    const isAuthRequest = 
      url.includes('/auth/') || 
      url.includes('/citizens/verify') || 
      url.includes('/citizens/request-code') || 
      url.includes('/citizens/register') ||
      url.includes('/citizens/login') ||
      url.includes('/citizens/forgot-password') ||
      url.includes('/citizens/reset-password') ||
      url.includes('/aspirant/signup') ||
      url.includes('/citizens/counties') ||
      url.includes('/citizens/constituencies') ||
      url.includes('/citizens/wards');
    
    if (!isAuthRequest) {
      const token = localStorage.getItem('token');
      
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
        
        console.log('🔑 JWT Token attached to request:', {
          url: config.url,
          tokenPreview: token.substring(0, 20) + '...'
        });
      } else {
        console.warn('⚠️ No token found for API request to:', config.url);
      }
    } else {
      console.log('🚫 Skipping token for public request:', url);
      delete config.headers['Authorization'];
    }
    
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response: any) => {
    return response;
  },
  async (error: any) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || '';
    
    console.error('❌ API Error:', {
      url,
      status: error.response?.status,
      method: originalRequest?.method,
      error: error.message
    });
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      
      if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
        console.log('🚫 Auth endpoint failed, not attempting refresh');
        clearAuthHelper();
        return Promise.reject(error);
      }
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        console.log('❌ No refresh token available, clearing auth');
        clearAuthHelper();
        return Promise.reject(error);
      }
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }
      
      originalRequest._retry = true;
      isRefreshing = true;
      
      try {
        console.log('🔄 Attempting token refresh...');
        
        const refreshResponse = await axios.post(
          `${API_URL}/api/v1/auth/refresh`,
          { refreshToken },
          {
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        
        console.log('📦 Refresh response:', refreshResponse.data);
        
        let newToken = null;
        let newRefreshToken = refreshToken;
        
        if (refreshResponse.data?.success && refreshResponse.data?.data?.token) {
          newToken = refreshResponse.data.data.token;
          newRefreshToken = refreshResponse.data.data.refreshToken || refreshToken;
        } else if (refreshResponse.data?.token) {
          newToken = refreshResponse.data.token;
          newRefreshToken = refreshResponse.data.refreshToken || refreshToken;
        } else if (refreshResponse.data?.data?.token) {
          newToken = refreshResponse.data.data.token;
          newRefreshToken = refreshResponse.data.data.refreshToken || refreshToken;
        }
        
        if (newToken) {
          localStorage.setItem('token', newToken);
          localStorage.setItem('refreshToken', newRefreshToken);
          
          console.log('✅ Token refreshed successfully');
          
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          processQueue(null, newToken);
          
          return api(originalRequest);
        } else {
          console.log('❌ Refresh response did not contain token');
          throw new Error('Invalid refresh response');
        }
        
      } catch (refreshError) {
        console.error('❌ Token refresh failed:', refreshError);
        clearAuthHelper();
        processQueue(refreshError, null);
        
        return Promise.reject(error);
        
      } finally {
        isRefreshing = false;
      }
    }
    
    if (error.response?.status === 403) {
      console.log('🚫 Access forbidden for:', url);
    }
    
    return Promise.reject(error);
  }
);

// ============ INTERFACE DEFINITIONS ============

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AspirantLoginCredentials {
  phoneNumber: string;
  otp: string;
}

export interface CitizenRegisterRequest {
  phoneNumber: string;
  fullName: string;
  email?: string;
  idNumber?: string;
  gender?: string;
  dateOfBirth?: string;
  countyId?: number;
  constituencyId?: number;
  wardId?: number;
  physicalAddress?: string;
  voterRegistrationNumber?: string;
  pollingStation?: string;
  languagePreference?: string;
  receiveSmsAlerts?: boolean;
  receiveEmailAlerts?: boolean;
}

export interface WardHeatMapDTO {
  wardId: number;
  wardName: string;
  wardCode: string;
  constituencyId?: number;
  constituencyName?: string;
  latitude?: number;
  longitude?: number;
  reportCount: number;
  citizenCount: number;
  avgUrgencyScore: number;
  pendingReports: number;
  resolvedReports: number;
  topIssueCategory: string;
  alertPriority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  increasePercentage: number;
  lastUpdated: string;
}

export interface CitizenLoginRequest {
  phoneNumber: string;
  verificationCode: string;
}

export interface CountyResponseDTO {
  id: number;
  name: string;
  code: string;
  capital: string;
  governorName: string;
}

export interface ConstituencyResponseDTO {
  id: number;
  name: string;
  code: string;
  countyId?: number;
  countyName?: string;
}

export interface WardResponseDTO {
  id: number;
  name: string;
  code: string;
  constituencyId?: number;
  constituencyName?: string;
}

export interface BaseResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
  downloadUrl?: string;
}

export interface AuthResponseData {
  token: string;
  refreshToken?: string;
  user?: {
    id: number;
    username: string;
    email: string;
    fullName: string;
    role: string;
    phoneNumber: string;
  };
  citizen?: any;
  aspirant?: any;
}

export interface Campaign {
  id: number;
  campaignName: string;
  status: string;
  type: string;
  cost: number;
  estimatedRecipients: number;
  language: string;
  aspirantName: string;
  description: string;
  content: string;
  summary?: string;
  aspirantId: number;
  targetWardIds?: number[];
  publishedAt?: string;
  createdAt: string;
  documentUrl?: string;
  videoUrl?: string;
  imageUrl?: string;
  feedbackLink?: string;
  smsRecipients?: number;
  [key: string]: any;
}

export interface Report {
  id: number;
  reportId: string;
  description: string;
  category: string;
  categoryDisplayName?: string;
  status: string;
  urgencyScore: number;
  phoneNumber: string;
  wardName?: string;
  constituencyName?: string;
  countyName?: string;
  reportedAt: string;
  [key: string]: any;
}

export interface ReportFilterParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
  wardId?: number;
  constituencyId?: number;
  countyId?: number;
  status?: string;
  category?: string;
  startDate?: string;
  endDate?: string;
  phoneNumber?: string;
  assignedTo?: string;
  minUrgencyScore?: number;
}

// ============ MANIFESTO INTERFACES ============

export interface ManifestoDTO {
  id: number;
  title: string;
  content: string;
  summary: string;
  visionStatement?: string;
  missionStatement?: string;
  source: 'CITIZEN_REPORTS' | 'CUSTOM' | 'TEMPLATE' | 'HYBRID';
  sourceDescription: string;
  status: 'DRAFT' | 'UNDER_REVIEW' | 'PUBLISHED' | 'ARCHIVED' | 'REVISED';
  statusDisplay: string;
  topIssueCategories: string[];
  sourceReportIds: string[];
  totalCitizenReportsAnalyzed?: number;
  documentUrl?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  isPublic: boolean;
  feedbackLink?: string;
  aspirantId: number;
  aspirantName: string;
  aspirantPosition: string;
  wardName?: string;
  constituencyName?: string;
  totalReportsInWard?: number;
  topIssueCategory?: string;
  totalReportsForTopIssue?: number;
  percentageOfTotalReports?: number;
}

export interface ManifestoAnalysisDTO {
  aspirantId: number;
  wardId: number;
  wardName: string;
  totalReports: number;
  issueCategoryCounts: Array<{
    category: string;
    categoryDisplay: string;
    count: number;
    percentage: number;
  }>;
  topIssueCategories: string[];
  averageUrgencyScore: number;
  pendingReports: number;
  resolvedReports: number;
}

export interface CreateManifestoRequest {
  title: string;
  content: string;
  summary: string;
  visionStatement?: string;
  missionStatement?: string;
  source?: 'CITIZEN_REPORTS' | 'CUSTOM' | 'TEMPLATE' | 'HYBRID';
  topIssueCategories?: string[];
  sourceReportIds?: string[];
  documentUrl?: string;
  feedbackLink?: string;
  isPublic?: boolean;
}

export interface GenerateManifestoRequest {
  title: string;
  customInstructions?: string;
  maxIssuesToInclude?: number;
  includeAllReports?: boolean;
  specificReportIds?: string[];
}

export interface UpdateManifestoRequest {
  title?: string;
  content?: string;
  summary?: string;
  visionStatement?: string;
  missionStatement?: string;
  topIssueCategories?: string[];
  documentUrl?: string;
  feedbackLink?: string;
  isPublic?: boolean;
}

// ============ HELPER FUNCTIONS ============

const getCurrentUserHelper = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

const getCurrentAspirantHelper = () => {
  try {
    const aspirantStr = localStorage.getItem('aspirant');
    if (aspirantStr) {
      return JSON.parse(aspirantStr);
    }
    
    const user = getCurrentUserHelper();
    if (user && user.role === 'ASPIRANT') {
      return user;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting current aspirant:', error);
    return null;
  }
};

const getCurrentCitizenHelper = () => {
  const citizen = localStorage.getItem('citizen');
  return citizen ? JSON.parse(citizen) : null;
};

const getCitizenIdHelper = (): number | null => {
  try {
    const currentCitizen = getCurrentCitizenHelper();
    if (currentCitizen && currentCitizen.id) {
      return currentCitizen.id;
    }
    
    const currentUser = getCurrentUserHelper();
    if (currentUser && currentUser.id && currentUser.role === 'CITIZEN') {
      return currentUser.id;
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.id && payload.role === 'CITIZEN') {
          return payload.id;
        }
      } catch (error) {
        console.error('Error parsing token payload:', error);
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error getting citizen ID:', error);
    return null;
  }
};

const getUserRoleHelper = (): string | null => {
  const user = getCurrentUserHelper();
  if (user && user.role) {
    return user.role;
  }
  
  const userStr = localStorage.getItem('user');
  if (userStr) {
    try {
      const parsedUser = JSON.parse(userStr);
      return parsedUser.role || null;
    } catch (error) {
      console.error('Error parsing user:', error);
    }
  }
  
  if (localStorage.getItem('aspirant')) {
    return 'ASPIRANT';
  }
  
  if (localStorage.getItem('citizen')) {
    return 'CITIZEN';
  }
  
  return null;
};

const clearAuthHelper = () => {
  console.log('🧹 Clearing auth data from localStorage');
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('aspirant');
  localStorage.removeItem('citizen');
  localStorage.removeItem('refreshToken');
};

const isAuthenticatedHelper = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

const getTokenHelper = (): string | null => {
  return localStorage.getItem('token');
};

const isTokenExpiredHelper = (): boolean => {
  const token = getTokenHelper();
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Date.now() / 1000;
    return payload.exp < currentTime;
  } catch (error) {
    console.error('❌ Error checking token expiration:', error);
    return true;
  }
};

const handleApiErrorHelper = (error: any, defaultMessage: string = 'An error occurred'): string => {
  console.error('API Error:', error);
  
  if (error.response) {
    const status = error.response.status;
    
    if (status === 401) {
      clearAuthHelper();
      return 'Session expired. Please login again.';
    }
    
    if (status === 403) {
      return 'You do not have permission to perform this action.';
    }
    
    if (status === 404) {
      return 'The requested resource was not found.';
    }
    
    if (status === 500) {
      return 'Server error. Please try again later.';
    }
    
    const errorData = error.response.data;
    if (errorData && errorData.error) {
      return errorData.error;
    }
    
    if (errorData && errorData.message) {
      return errorData.message;
    }
  }
  
  if (error.request) {
    return 'No response from server. Please check your connection.';
  }
  
  return error.message || defaultMessage;
};

// ============ ASPIRANT ID HELPER FUNCTIONS ============

export const getAspirantId = (): number | null => {
  try {
    console.log('🔍 getAspirantId called');
    
    const aspirantStr = localStorage.getItem('aspirant');
    if (aspirantStr) {
      try {
        const aspirantData = JSON.parse(aspirantStr);
        if (aspirantData.id) {
          console.log('✅ Found aspirant ID from aspirant object:', aspirantData.id);
          return Number(aspirantData.id);
        }
      } catch (e) {
        console.error('Error parsing aspirant data:', e);
      }
    }
    
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('🔍 Token payload:', payload);
        
        if (payload.aspirantId) {
          console.log('✅ Found aspirant ID from token aspirantId field:', payload.aspirantId);
          
          const aspirantObj = {
            id: payload.aspirantId,
            name: payload.name || 'Aspirant',
            phoneNumber: payload.phoneNumber || '',
            position: payload.position || 'Aspirant'
          };
          localStorage.setItem('aspirant', JSON.stringify(aspirantObj));
          
          return Number(payload.aspirantId);
        }
        
        if (payload.id && payload.role === 'ASPIRANT') {
          console.log('⚠️ Token has ASPIRANT role with user ID:', payload.id);
        }
      } catch (e) {
        console.error('Error decoding token:', e);
      }
    }
    
    console.log('❌ Could not find aspirant ID from any source');
    return null;
  } catch (error) {
    console.error('Error getting aspirant ID:', error);
    return null;
  }
};

// ============ MEMORANDUM FETCHING FUNCTIONS ============

const formatMemorandumData = (memo: any, aspirantId: number): Memorandum => {
  return {
    id: memo.id || 0,
    memorandumNumber: memo.memorandumNumber || `MEM-${memo.id || Date.now()}`,
    aspirantId: memo.aspirantId || aspirantId,
    aspirantName: memo.aspirantName || '',
    type: memo.type || 'COUNTY_GOVERNMENT',
    recipientDepartment: memo.recipientDepartment || '',
    subject: memo.subject || '',
    content: memo.content || '',
    evidenceSummary: memo.evidenceSummary || '',
    affectedWards: memo.affectedWards || [],
    estimatedCost: memo.estimatedCost || 0,
    costBreakdown: memo.costBreakdown || '',
    status: memo.status || 'DRAFT',
    generatedAt: memo.generatedAt || new Date().toISOString(),
    downloadUrl: memo.downloadUrl || '',
    filename: memo.filename || `MEMORANDUM-${memo.memorandumNumber || memo.id}.pdf`
  };
};

const extractFilenameFromUrl = (url: string): string => {
  if (!url) return '';
  try {
    const parts = url.split('/');
    return parts[parts.length - 1] || '';
  } catch (error) {
    return '';
  }
};

export const fetchAspirantMemorandums = async (aspirantId: number): Promise<{ success: boolean; data?: Memorandum[]; error?: string }> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return { success: false, error: 'No authentication token found' };
    }

    console.log(`🔍 Attempting to fetch memorandums for aspirant ${aspirantId}`);
    
    const endpoints = [
      `${API_BASE_URL}/api/v1/memorandums/aspirant/${aspirantId}`,
      `${API_BASE_URL}/api/v1/memorandums/aspirantId/${aspirantId}`,
      `${API_BASE_URL}/api/v1/memorandums/aspirant?id=${aspirantId}`,
      `${API_BASE_URL}/api/v1/memorandums/my-memorandums`,
      `${API_BASE_URL}/api/v1/memorandums?aspirantId=${aspirantId}`,
      `${API_BASE_URL}/api/v1/memorandums?aspirant_id=${aspirantId}`,
      `${API_BASE_URL}/api/v1/memorandums/aspirant/${aspirantId}/all`,
      `${API_BASE_URL}/api/v1/memorandums/list/${aspirantId}`,
    ];

    for (const endpoint of endpoints) {
      try {
        console.log(`Trying endpoint: ${endpoint}`);
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          console.log(`✅ Success with endpoint: ${endpoint}`, data);
          
          let memorandums: Memorandum[] = [];
          
          if (data.success && Array.isArray(data.data)) {
            memorandums = data.data.map((memo: any) => formatMemorandumData(memo, aspirantId));
          } else if (Array.isArray(data)) {
            memorandums = data.map((memo: any) => formatMemorandumData(memo, aspirantId));
          } else if (data.data && Array.isArray(data.data)) {
            memorandums = data.data.map((memo: any) => formatMemorandumData(memo, aspirantId));
          } else if (data.memorandums && Array.isArray(data.memorandums)) {
            memorandums = data.memorandums.map((memo: any) => formatMemorandumData(memo, aspirantId));
          }

          if (memorandums.length > 0) {
            return { success: true, data: memorandums };
          } else {
            console.log(`Endpoint returned empty array, trying next...`);
            continue;
          }
        }
      } catch (error) {
        console.log(`Endpoint ${endpoint} failed:`, error);
        continue;
      }
    }

    return { success: false, error: 'Could not fetch memorandums from any endpoint' };
  } catch (error) {
    console.error('Error in fetchAspirantMemorandums:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

export const getAspirantMemorandums = async (aspirantId: number): Promise<{ data: ApiResponse<Memorandum[]> }> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      throw new Error('No authentication token found');
    }

    console.log(`📞 Fetching memorandums from: ${API_BASE_URL}/api/v1/memorandums/aspirant/${aspirantId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/aspirant/${aspirantId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired. Please login again.');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Memorandums fetched successfully:', data);
    
    if (data.success && Array.isArray(data.data)) {
      data.data = data.data.map((memo: any) => formatMemorandumData(memo, aspirantId));
    }
    
    return { data };
  } catch (error) {
    console.error('❌ Error fetching memorandums:', error);
    throw error;
  }
};

export const getMemorandum = async (id: string): Promise<{ data: ApiResponse<Memorandum> }> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching memorandum:', error);
    throw error;
  }
};

export const createMemorandum = async (data: MemorandumRequest): Promise<{ data: ApiResponse<Memorandum> }> => {
  try {
    const token = localStorage.getItem('token');
    
    console.log('📤 Creating memorandum with data:', data);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired. Please login again.');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const responseData = await response.json();
    console.log('✅ Memorandum created:', responseData);
    return { data: responseData };
  } catch (error) {
    console.error('❌ Error creating memorandum:', error);
    throw error;
  }
};

export const updateMemorandumStatus = async (id: string, status: MemorandumStatus): Promise<{ data: ApiResponse<Memorandum> }> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/${id}/status`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error updating memorandum status:', error);
    throw error;
  }
};

export const deleteMemorandum = async (id: string): Promise<{ data: ApiResponse }> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error deleting memorandum:', error);
    throw error;
  }
};

export const getMemorandumStatsByAspirant = async (aspirantId: string | number): Promise<{ data: ApiResponse<MemorandumStats> }> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/aspirant/${aspirantId}/stats`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching memorandum stats:', error);
    throw error;
  }
};

export const getMyMemorandumStats = async (): Promise<{ data: ApiResponse<MemorandumStats> }> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/stats/my-stats`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching memorandum stats:', error);
    throw error;
  }
};

export const downloadMemorandum = async (id: string): Promise<{ data: Blob }> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/${id}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const blob = await response.blob();
    return { data: blob };
  } catch (error) {
    console.error('Error downloading memorandum:', error);
    throw error;
  }
};

export const downloadMemorandumBlob = async (id: string): Promise<Blob> => {
  const token = localStorage.getItem('token');
  
  if (!token) {
    throw new Error('No authentication token found');
  }

  const response = await fetch(`${API_BASE_URL}/api/v1/memorandums/${id}/download`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      clearAuthHelper();
      throw new Error('Session expired. Please login again.');
    }
    throw new Error(`HTTP ${response.status}`);
  }

  return response.blob();
};

export const createMemorandumDirect = async (data: MemorandumRequest): Promise<any> => {
  try {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_BASE_URL}/api/v1/memorandums`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Direct memorandum creation failed:', error);
    throw error;
  }
};

// ============ CAMPAIGN API FUNCTIONS ============

export const testCampaignEndpoint = async (): Promise<boolean> => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/test`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      console.error('❌ Test endpoint failed:', response.status);
      return false;
    }
    
    const data = await response.json();
    console.log('✅ Test endpoint response:', data);
    return data.success === true;
  } catch (error) {
    console.error('❌ Test endpoint error:', error);
    return false;
  }
};

export const createCampaignDirect = async (aspirantId: number, campaignData: any) => {
  try {
    const token = localStorage.getItem('token');
    
    const url = `${API_BASE_URL}/api/v1/campaigns/aspirant/${aspirantId}`;
    console.log('📤 Sending campaign creation request to:', url);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(campaignData)
    });

    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired. Please login again.');
      }
      
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('✅ Campaign created:', data);
    return data;
  } catch (error) {
    console.error('❌ Direct campaign creation failed:', error);
    throw error;
  }
};

export const getAspirantCampaigns = async (aspirantId: number) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/aspirant/${aspirantId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

export const createAspirantCampaign = async (aspirantId: string, campaignData: any) => {
  const { aspirantId: _, ...cleanData } = campaignData;
  return api.post(`/v1/campaigns/aspirant/${aspirantId}`, cleanData);
};

export const createCampaignFallback = async (aspirantId: string, campaignData: any) => {
  try {
    return await createAspirantCampaign(aspirantId, campaignData);
  } catch (error) {
    console.log('Primary campaign creation failed, trying fallback...', error);
    return await createCampaignDirect(Number(aspirantId), campaignData);
  }
};

export const getPublishedCampaignsByWard = async (wardId: number): Promise<{ data: ApiResponse<Campaign[]> }> => {
  try {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`📢 Fetching published campaigns for ward: ${wardId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/public/ward/${wardId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        console.log('⚠️ Auth failed, trying public access...');
        const publicResponse = await fetch(`${API_BASE_URL}/api/v1/campaigns/public/ward/${wardId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!publicResponse.ok) {
          throw new Error(`HTTP ${publicResponse.status}`);
        }
        
        const publicData = await publicResponse.json();
        return { data: publicData };
      }
      
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('❌ Error fetching published campaigns:', error);
    throw error;
  }
};

export const getRecentCampaigns = async (limit: number = 10): Promise<{ data: ApiResponse<Campaign[]> }> => {
  try {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`📢 Fetching recent campaigns (limit: ${limit})`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/recent?limit=${limit}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('❌ Error fetching recent campaigns:', error);
    throw error;
  }
};

export const getAllCampaigns = async (params?: Record<string, any>): Promise<{ data: ApiResponse<Campaign[]> }> => {
  try {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let queryString = '';
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      queryString = `?${searchParams.toString()}`;
    }

    console.log(`📢 Fetching all campaigns${queryString}`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/campaigns${queryString}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('❌ Error fetching campaigns:', error);
    throw error;
  }
};

export const getCampaignById = async (id: string | number): Promise<{ data: ApiResponse<Campaign> }> => {
  try {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`📢 Fetching campaign: ${id}`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/campaigns/${id}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('❌ Error fetching campaign:', error);
    throw error;
  }
};

// ============ MANIFESTO API FUNCTIONS ============

export const getManifestoAnalysis = async (aspirantId: number) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/analysis`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching manifesto analysis:', error);
    throw error;
  }
};

export const generateManifestoFromReports = async (aspirantId: number, requestData: any) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/generate-from-reports`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error generating manifesto:', error);
    throw error;
  }
};

export const createManifesto = async (aspirantId: number, requestData: any) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/create`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error creating manifesto:', error);
    throw error;
  }
};

export const getAspirantManifestos = async (aspirantId: number) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching manifestos:', error);
    throw error;
  }
};

export const getManifestoById = async (aspirantId: number, manifestoId: number) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/${manifestoId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching manifesto:', error);
    throw error;
  }
};

export const updateManifesto = async (aspirantId: number, manifestoId: number, requestData: any) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/${manifestoId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error updating manifesto:', error);
    throw error;
  }
};

export const publishManifesto = async (aspirantId: number, manifestoId: number) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/${manifestoId}/publish`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error publishing manifesto:', error);
    throw error;
  }
};

export const deleteManifesto = async (aspirantId: number, manifestoId: number) => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/aspirant/${aspirantId}/${manifestoId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error deleting manifesto:', error);
    throw error;
  }
};

export const getPublicManifestosByWard = async (wardId: number) => {
  try {
    console.log(`📜 Fetching public manifestos for ward: ${wardId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/public/ward/${wardId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error fetching public manifestos:', error);
    throw error;
  }
};

export const getPublicManifestosByWardAxios = async (wardId: number) => {
  try {
    const publicApi = axios.create({
      baseURL: `${API_URL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });
    
    delete publicApi.defaults.headers.common['Authorization'];
    
    console.log(`📜 Fetching public manifestos for ward: ${wardId} (axios public instance)`);
    const response = await publicApi.get(`/v1/manifestos/public/ward/${wardId}`);
    return response;
  } catch (error) {
    console.error('Error fetching public manifestos via axios:', error);
    throw error;
  }
};

export const testManifestoEndpoint = async () => {
  try {
    const token = localStorage.getItem('token');
    
    const response = await fetch(`${API_BASE_URL}/api/v1/manifestos/test`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return { data };
  } catch (error) {
    console.error('Error testing manifesto endpoint:', error);
    throw error;
  }
};

// ============ ASPIRANT DETAILS FUNCTION ============

export const getAspirantDetails = async (aspirantId: string): Promise<{ success: boolean; data?: any; error?: string }> => {
  try {
    const token = localStorage.getItem('token');
    
    if (!token) {
      return { success: false, error: 'No authentication token found' };
    }

    console.log(`📞 Fetching aspirant details for ID: ${aspirantId}`);
    
    const response = await fetch(`${API_BASE_URL}/api/v1/aspirants/${aspirantId}/with-ward`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Aspirant details fetched successfully:', data);
      
      if (data.success && data.data) {
        return { success: true, data: data.data };
      }
      return { success: true, data: data.data || data };
    }

    const fallbackResponse = await fetch(`${API_BASE_URL}/api/v1/aspirants/${aspirantId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (fallbackResponse.ok) {
      const data = await fallbackResponse.json();
      console.log('✅ Aspirant details fetched from fallback:', data);
      
      if (data.success && data.data) {
        return { success: true, data: data.data };
      }
      return { success: true, data: data.data || data };
    }

    if (response.status === 401 || fallbackResponse.status === 401) {
      return { success: false, error: 'Session expired. Please login again.' };
    }

    return { success: false, error: `Failed to fetch aspirant details: ${response.status}` };
  } catch (error) {
    console.error('❌ Error fetching aspirant details:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error occurred' 
    };
  }
};

// ============ MAIN API SERVICE ============

export const apiService = {
  // ============ ASPIRANT DETAILS ============
  getAspirantDetails: (id: string) => getAspirantDetails(id),
  
  // ============ DASHBOARD ENDPOINTS ============
  getAspirantDashboard: (aspirantId: string | number): Promise<any> =>
    api.get<BaseResponse<any>>(`/v1/dashboard/aspirant/${aspirantId}`),

  getDashboardStats: (aspirantId: string | number): Promise<any> =>
    api.get<BaseResponse<any>>(`/v1/citizen-reports/dashboard/stats?aspirantId=${aspirantId}`),

  testDashboardEndpoint: (): Promise<any> =>
    api.get<BaseResponse>('/v1/dashboard/test'),

  // ============ MANIFESTO ENDPOINTS ============
  getManifestoAnalysis: (aspirantId: string | number): Promise<any> =>
    api.get(`/v1/manifestos/aspirant/${aspirantId}/analysis`),
  
  generateManifestoFromReports: (aspirantId: string | number, data: GenerateManifestoRequest): Promise<any> =>
    api.post(`/v1/manifestos/aspirant/${aspirantId}/generate-from-reports`, data),
  
  createManifesto: (aspirantId: string | number, data: CreateManifestoRequest): Promise<any> =>
    api.post(`/v1/manifestos/aspirant/${aspirantId}/create`, data),
  
  getAspirantManifestos: (aspirantId: string | number): Promise<any> =>
    api.get(`/v1/manifestos/aspirant/${aspirantId}`),
  
  getManifestoById: (aspirantId: string | number, manifestoId: string | number): Promise<any> =>
    api.get(`/v1/manifestos/aspirant/${aspirantId}/${manifestoId}`),
  
  updateManifesto: (aspirantId: string | number, manifestoId: string | number, data: UpdateManifestoRequest): Promise<any> =>
    api.put(`/v1/manifestos/aspirant/${aspirantId}/${manifestoId}`, data),
  
  publishManifesto: (aspirantId: string | number, manifestoId: string | number): Promise<any> =>
    api.post(`/v1/manifestos/aspirant/${aspirantId}/${manifestoId}/publish`, {}),
  
  deleteManifesto: (aspirantId: string | number, manifestoId: string | number): Promise<any> =>
    api.delete(`/v1/manifestos/aspirant/${aspirantId}/${manifestoId}`),
  
  getPublicManifestos: (wardId: string | number): Promise<any> =>
    getPublicManifestosByWardAxios(Number(wardId)),
  
  getPublicManifestosByWard: (wardId: string | number): Promise<any> =>
    getPublicManifestosByWardAxios(Number(wardId)),
  
  testManifestoEndpoint: (): Promise<any> =>
    api.get('/v1/manifestos/test'),

  // ============ LOCATION ENDPOINTS ============
  getCounties: () => api.get<BaseResponse<CountyResponseDTO[]>>('/v1/citizens/counties'),
  
  getConstituenciesByCounty: (countyId: number) => 
    api.get<BaseResponse<ConstituencyResponseDTO[]>>(`/v1/citizens/counties/${countyId}/constituencies`),
  
  getWardsByConstituency: (constituencyId: number) => 
    api.get<BaseResponse<WardResponseDTO[]>>(`/v1/citizens/constituencies/${constituencyId}/wards`),
  
  getWardCitizenCount: async (wardId: number): Promise<number> => {
    try {
      const response = await api.get<BaseResponse<number>>(`/v1/citizens/ward/${wardId}/count`);
      if (response.data.success) {
        return response.data.data || 0;
      }
      return 0;
    } catch (error) {
      console.log('Error getting ward count:', error);
      return 0;
    }
  },
    
  getVerifiedPhoneNumbersByWard: (wardId: number) =>
    api.get<BaseResponse<string[]>>(`/v1/citizens/ward/${wardId}/verified-phone-numbers`),
    
  getVerifiedPhoneNumbersByWardIds: (wardIds: number[]) =>
    api.post<BaseResponse<string[]>>(`/v1/citizens/wards/verified-phone-numbers`, { wardIds }),
  
  // ============ ASPIRANT ENDPOINTS ============
  getAspirantWithWard: (id: string) => 
    api.get<BaseResponse<any>>(`/v1/aspirants/${id}/with-ward`),
    
  getAspirantDebug: (id: string) =>
    api.get<BaseResponse<any>>(`/v1/aspirants/${id}/debug`),
    
  testAspirant: (id: string) =>
    api.get<BaseResponse<any>>(`/v1/aspirants/${id}/test`),
    
  getCitizenCountByWard: async (wardId: number): Promise<number> => {
    return apiService.getWardCitizenCount(wardId);
  },

  // ============ CITIZEN AUTH ENDPOINTS ============
  citizenLoginWithEmailPassword: (data: { email: string; password: string }) => 
    api.post<BaseResponse<AuthResponseData>>('/v1/citizens/login', data),

  requestPasswordReset: (email: string) => 
    api.post<BaseResponse>('/v1/citizens/forgot-password', { email }),

  resetCitizenPassword: (email: string, otp: string, newPassword: string) => 
    api.post<BaseResponse>('/v1/citizens/reset-password', { email, otp, newPassword }),

  registerCitizen: (data: CitizenRegisterRequest) => 
    api.post<BaseResponse>('/v1/citizens/register', data),

  verifyCitizen: (data: CitizenLoginRequest) => 
    api.post<BaseResponse<AuthResponseData>>('/v1/citizens/verify', data),

  requestVerificationCode: (phoneNumber: string) => 
    api.post<BaseResponse>('/v1/citizens/request-code', { phoneNumber }),

  // ============ ENHANCED CITIZEN REPORT METHODS ============
  getCitizenById: (id: number) => 
    api.get<BaseResponse>(`/v1/citizens/${id}`),

  getCitizenReportsById: (citizenId: number) => 
    api.get<BaseResponse<Report[]>>(`/v1/citizen-reports/citizen-id/${citizenId}`),

  getReportsByUserId: (userId: number) => 
    api.get<BaseResponse<Report[]>>(`/v1/citizen-reports/user/${userId}`),

  getCitizenReportsEnhanced: async (phoneNumber: string) => {
    try {
      const response = await api.get<BaseResponse<Report[]>>(`/v1/citizen-reports/citizen/${phoneNumber}`);
      return response;
    } catch (error: any) {
      if (error.response?.status === 401) {
        const encodedPhone = encodeURIComponent(phoneNumber);
        const altResponse = await api.get<BaseResponse<Report[]>>(`/v1/citizen-reports/phone/${encodedPhone}`);
        return altResponse;
      }
      throw error;
    }
  },

  getCitizenReportsWithFallback: async (phoneNumber: string, citizenId?: number) => {
    try {
      const response = await apiService.getCitizenReports(phoneNumber);
      if (response.data.success && response.data.data && response.data.data.length > 0) {
        return response;
      }
      
      if (citizenId) {
        const idResponse = await apiService.getCitizenReportsById(citizenId);
        if (idResponse.data.success && idResponse.data.data && idResponse.data.data.length > 0) {
          return idResponse;
        }
      }
      
      const enhancedResponse = await apiService.getCitizenReportsEnhanced(phoneNumber);
      return enhancedResponse;
    } catch (error) {
      console.error('All report fetching methods failed:', error);
      throw error;
    }
  },

  // ============ HEAT MAP ENDPOINTS ============
  getHeatMapAll: () => api.get<BaseResponse<WardHeatMapDTO[]>>('/v1/heatmap/all'),

  getMyWardHeatMap: () => api.get<BaseResponse<WardHeatMapDTO>>('/v1/heatmap/my-ward'),

  getWardHeatMap: (wardId: number) => 
    api.get<BaseResponse<WardHeatMapDTO>>(`/v1/heatmap/ward/${wardId}`),

  // ============ ATTACHMENT ENDPOINTS ============
  createReportWithAttachments: (formData: FormData): Promise<any> => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/api/v1/citizen-reports/add-with-attachments`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData
    }).then(response => {
      if (!response.ok) {
        return response.text().then(text => {
          throw new Error(`HTTP ${response.status}: ${text}`);
        });
      }
      return response.json();
    });
  },

  getAttachment: (reportId: string, attachmentIndex: number): Promise<Blob> => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachment/${attachmentIndex}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.blob();
    });
  },

  getAttachmentsInfo: (reportId: string): Promise<any> => {
    const token = localStorage.getItem('token');
    return fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}/attachments`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      return response.json();
    });
  },

  // ============ AUTH ENDPOINTS ============
  login: (credentials: LoginCredentials) => 
    api.post<BaseResponse<AuthResponseData>>('/v1/auth/login', credentials),
  
  sendAspirantOtp: (phoneNumber: string) => 
    api.post<BaseResponse>('/v1/auth/aspirant/send-otp', null, { params: { phoneNumber } }),
  
  aspirantLogin: (data: AspirantLoginCredentials) => 
    api.post<BaseResponse<AuthResponseData>>('/v1/auth/aspirant/login', data),
  
  aspirantSignup: (data: any) => 
    api.post<BaseResponse>('/v1/auth/aspirant/signup', data),
  
  logout: () => api.post<BaseResponse>('/v1/auth/logout'),
  
  refreshToken: (refreshToken: string) => 
    api.post<BaseResponse>('/v1/auth/refresh', { refreshToken }),
  
  getCitizenProfile: () => api.get<BaseResponse>('/v1/citizens/profile'),
  
  getCitizen: (id: string) => api.get<BaseResponse>(`/v1/citizens/${id}`),
  
  getCitizenByPhone: (phoneNumber: string) => 
    api.get<BaseResponse>(`/v1/citizens/phone/${phoneNumber}`),
  
  updateCitizenProfile: (id: string, data: any) => 
    api.put<BaseResponse>(`/v1/citizens/${id}`, data),
  
  // ============ ASPIRANT MANAGEMENT ENDPOINTS ============
  getAspirants: () => api.get<BaseResponse<any[]>>('/v1/aspirants'),
  
  getAspirant: (id: string, includeWard: boolean = false) => 
    api.get<BaseResponse<any>>(`/v1/aspirants/${id}${includeWard ? '?includeWard=true' : ''}`),
  
  createAspirant: (data: any) => api.post<BaseResponse>('/v1/aspirants', data),
  
  updateAspirant: (id: string, data: any) => api.put<BaseResponse>(`/v1/aspirants/${id}`, data),
  
  deleteAspirant: (id: string) => api.delete<BaseResponse>(`/v1/aspirants/${id}`),
  
  // ============ ASPIRANT WARD ENDPOINTS ============
  getAspirantsByWard: (wardId: number) => 
    api.get<BaseResponse<any[]>>(`/v1/aspirants/ward/${wardId}`),
  
  // ============ CAMPAIGN ENDPOINTS ============
  testCampaignEndpoint: () => api.get<BaseResponse>('/v1/campaigns/test'),
  
  getCampaigns: () => api.get<BaseResponse<Campaign[] | any>>('/v1/campaigns'),
  
  getCampaign: (id: string) => api.get<BaseResponse>(`/v1/campaigns/${id}`),
  
  createCampaign: (aspirantId: string, data: any) => {
    const { aspirantId: _, ...cleanData } = data;
    return api.post<BaseResponse>(`/v1/campaigns/aspirant/${aspirantId}`, cleanData);
  },
  
  createAspirantCampaign: createAspirantCampaign,
  
  createCampaignFallback: createCampaignFallback,
  
  createCampaignDirect: createCampaignDirect,
  
  testCampaignConnection: testCampaignEndpoint,
  
  getAspirantCampaigns: (aspirantId: string) => 
    api.get<BaseResponse>(`/v1/campaigns/aspirant/${aspirantId}`),
  
  // ============ CAMPAIGN PUBLIC METHODS FOR COMMUNITY ============
  getPublishedCampaignsByWard: (wardId: number) => 
    getPublishedCampaignsByWard(wardId),
  
  getRecentCampaigns: (limit?: number) => 
    getRecentCampaigns(limit),
  
  getAllCampaigns: (params?: Record<string, any>) => 
    getAllCampaigns(params),
  
  getCampaignById: (id: string | number) => 
    getCampaignById(id),

  // ============ ID VERIFICATION ENDPOINTS ============
  saveVerification: (data: any) => 
    api.post<BaseResponse>('/v1/verification/save', data),

  getVerificationStatus: (verificationId: string) => 
    api.get<BaseResponse>(`/v1/verification/status/${verificationId}`),

  getCitizenVerification: (phoneNumber: string) => 
    api.get<BaseResponse>(`/v1/verification/citizen/${phoneNumber}`),

  checkVerificationStatus: (phoneNumber: string) => 
    api.get<BaseResponse>(`/v1/verification/check/${phoneNumber}`),
  
  // ============ MEMORANDUM ENDPOINTS ============
  fetchAspirantMemorandums: (aspirantId: number) => fetchAspirantMemorandums(aspirantId),
  getMemorandums: () => api.get<ApiResponse<Memorandum[]>>('/v1/memorandums'),
  getMyMemorandums: () => api.get<ApiResponse<Memorandum[]>>('/v1/memorandums/my-memorandums'),
  getAspirantMemorandums: (aspirantId: number) => 
    api.get<ApiResponse<Memorandum[]>>(`/v1/memorandums/aspirant/${aspirantId}`),
  getMemorandum: (id: string) => api.get<ApiResponse<Memorandum>>(`/v1/memorandums/${id}`),
  createMemorandum: (data: MemorandumRequest) => 
    api.post<ApiResponse<Memorandum>>('/v1/memorandums', data),
  updateMemorandum: (id: string, data: MemorandumRequest) => 
    api.put<ApiResponse<Memorandum>>(`/v1/memorandums/${id}`, data),
  updateMemorandumStatus: (memorandumId: string | number, status: MemorandumStatus) =>
    api.patch<ApiResponse<Memorandum>>(`/v1/memorandums/${memorandumId}/status`, { status }),
  getMemorandumStatsByAspirant: (aspirantId: string | number) =>
    api.get<ApiResponse<MemorandumStats>>(`/v1/memorandums/aspirant/${aspirantId}/stats`),
  getMemorandumStats: () =>
    api.get<ApiResponse<MemorandumStats>>('/v1/memorandums/stats/my-stats'),
  getMyMemorandumStats: () =>
    api.get<ApiResponse<MemorandumStats>>('/v1/memorandums/stats/my-stats'),
  deleteMemorandum: (id: string) => api.delete<ApiResponse>(`/v1/memorandums/${id}`),
  downloadMemorandum: (id: string) => 
    api.get(`/v1/memorandums/${id}/download`, { responseType: 'blob' }),
  createMemorandumDirect: createMemorandumDirect,
  
  // ============ REPORT ENDPOINTS ============
  getReports: (params?: ReportFilterParams) => 
    api.get<BaseResponse<Report[]>>('/v1/citizen-reports', { params }),
  
  getReport: (id: string) => 
    api.get<BaseResponse>(`/v1/citizen-reports/${id}`),
  
  createReport: (data: any) => 
    api.post<BaseResponse>('/v1/citizen-reports/add', data),
  
  updateReportStatus: (reportId: string, data: any) => 
    api.patch<BaseResponse>(`/v1/citizen-reports/${reportId}/status`, data),
  
  searchReports: (params: ReportFilterParams) => 
    api.get<BaseResponse<Report[]>>('/v1/citizen-reports/search', { params }),
  
  getCitizenReports: (phoneNumber: string) => 
    api.get<BaseResponse<Report[]>>(`/v1/citizen-reports/citizen/${phoneNumber}`),
  
  getUrgentReports: (minScore: number = 7) => 
    api.get<BaseResponse<Report[]>>('/v1/citizen-reports/urgent', { params: { minScore } }),

  getAspirantReports: (aspirantId: number) => 
    api.get<BaseResponse<any[]>>(`/v1/citizen-reports/aspirant/${aspirantId}`),
  
  getReportsByWard: (wardId: number) => 
    api.get<BaseResponse<any[]>>(`/v1/citizen-reports/ward/${wardId}`),
  
  getReportsByCounty: (countyId: number) => 
    api.get<BaseResponse<any[]>>(`/v1/citizen-reports/county/${countyId}`),
  
  getAspirantDashboardStats: (aspirantId: number) =>
    api.get<BaseResponse<any>>(`/v1/citizen-reports/dashboard/stats?aspirantId=${aspirantId}`),
  
  // ============ COMMUNITY MANIFESTO ENDPOINTS ============
  getManifestosByAspirant: (aspirantId: number) => 
    api.get<BaseResponse<any[]>>(`/v1/campaigns/manifesto/aspirant/${aspirantId}`),
  
  // ============ MEMORANDUM COMMUNITY ENDPOINTS ============
  getMemorandumsByAspirant: (aspirantId: number) => 
    api.get<BaseResponse<any[]>>(`/v1/memorandums/aspirant/${aspirantId}`),
  
  // ============ TEMPLATE ENDPOINTS ============
  getTemplates: () => api.get<BaseResponse>('/v1/templates'),
  
  getTemplate: (templateName: string) => 
    api.get<BaseResponse>(`/v1/templates/${templateName}`),
  
  getTemplatePreview: (templateName: string) => 
    api.get<BaseResponse>(`/v1/templates/${templateName}/preview`),
  
  createTemplate: (data: any) => api.post<BaseResponse>('/v1/templates/custom', data),
  
  // ============ WARD & CONSTITUENCY ENDPOINTS ============
  getWards: () => api.get<BaseResponse>('/v1/wards'),
  
  getWard: (id: string) => api.get<BaseResponse>(`/v1/wards/${id}`),
  
  getConstituencies: () => api.get<BaseResponse>('/v1/constituencies'),
  
  getConstituency: (id: string) => api.get<BaseResponse>(`/v1/constituencies/${id}`),
  
  // ============ COUNTY ENDPOINTS ============
  getCountiesPublic: () => api.get<BaseResponse>('/v1/counties'),
  
  getCounty: (id: string) => api.get<BaseResponse>(`/v1/counties/${id}`),
  
  // ============ ISSUE CATEGORIES ENDPOINTS ============
  getIssueCategories: () => api.get<BaseResponse>('/v1/issue-categories'),
  
  // ============ HEALTH & TEST ENDPOINTS ============
  healthCheck: () => api.get('/actuator/health'),
  
  testEndpoint: () => api.get<BaseResponse>('/v1/citizen-reports/test'),
  
  citizenHealthCheck: () => api.get<BaseResponse>('/v1/citizens/health'),
  
  citizenTest: () => api.get<BaseResponse>('/v1/citizens/test'),
  
  // ============ UTILITY METHODS ============
  clearAuth: clearAuthHelper,
  
  isAuthenticated: isAuthenticatedHelper,
  
  getCurrentUser: getCurrentUserHelper,
  
  getCurrentAspirant: getCurrentAspirantHelper,
  
  getCurrentCitizen: getCurrentCitizenHelper,
  
  getAspirantId: getAspirantId,
  
  getCitizenId: getCitizenIdHelper,
  
  getUserRole: getUserRoleHelper,
  
  getToken: getTokenHelper,
  
  isTokenExpired: isTokenExpiredHelper,

  clearTokenForAuthRequest: () => {
    const token = getTokenHelper();
    if (token) {
      console.log('🧹 Temporarily removing token for auth request');
      localStorage.removeItem('token');
      return token;
    }
    return null;
  },

  restoreToken: (token: string) => {
    if (token) {
      localStorage.setItem('token', token);
      console.log('✅ Token restored after auth request');
    }
  },

  makeAuthRequest: async (requestFn: () => Promise<any>) => {
    const currentToken = getTokenHelper();
    
    if (currentToken) {
      localStorage.removeItem('token');
    }
    
    try {
      const response = await requestFn();
      return response;
    } finally {
      if (currentToken) {
        localStorage.setItem('token', currentToken);
      }
    }
  },

  handleApiError: handleApiErrorHelper,

  testApiConnection: async () => {
    try {
      const response = await api.get('/actuator/health');
      return { success: true, data: response.data };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.message,
        status: error.response?.status 
      };
    }
  },
  
  testAllEndpoints: async () => {
    const tests = [
      { name: 'Health Check', test: () => apiService.healthCheck() },
      { name: 'Campaign Test', test: () => apiService.testCampaignEndpoint() },
      { name: 'Reports Test', test: () => apiService.testEndpoint() },
      { name: 'Citizen Health', test: () => apiService.citizenHealthCheck() },
      { name: 'Manifesto Test', test: () => apiService.testManifestoEndpoint() },
      { name: 'Dashboard Test', test: () => apiService.testDashboardEndpoint() },
      { name: 'Get Campaigns', test: () => apiService.getCampaigns() },
      { name: 'Get Aspirants', test: () => apiService.getAspirants() },
      { name: 'Get Counties', test: () => apiService.getCounties() },
    ];
    
    const results = [];
    
    for (const test of tests) {
      try {
        const response = await test.test();
        results.push({
          name: test.name,
          status: '✅ Success',
          code: response.status,
        });
      } catch (error: any) {
        results.push({
          name: test.name,
          status: '❌ Failed',
          code: error.response?.status || 'No response',
          error: error.message,
        });
      }
    }
    
    console.table(results);
    return results;
  },
  
  directFetch: async (url: string, method: string = 'GET', body?: any) => {
    const token = localStorage.getItem('token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const options: RequestInit = {
      method,
      headers,
    };
    
    if (body) {
      options.body = JSON.stringify(body);
    }
    
    const fullUrl = url.startsWith('/') ? `${API_BASE_URL}${url}` : `${API_BASE_URL}/${url}`;
    const response = await fetch(fullUrl, options);
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    return response.json();
  },

  fetchCounties: async (): Promise<CountyResponseDTO[]> => {
    try {
      const response = await apiService.getCounties();
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching counties:', error);
      return [];
    }
  },

  fetchConstituencies: async (countyId: number): Promise<ConstituencyResponseDTO[]> => {
    try {
      const response = await apiService.getConstituenciesByCounty(countyId);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching constituencies:', error);
      return [];
    }
  },

  fetchWards: async (constituencyId: number): Promise<WardResponseDTO[]> => {
    try {
      const response = await apiService.getWardsByConstituency(constituencyId);
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching wards:', error);
      return [];
    }
  },
};

// ============ EXPORTED HELPER FUNCTIONS ============

export const getCitizenId = getCitizenIdHelper;
export const getUserRole = getUserRoleHelper;
export const isAuthenticated = isAuthenticatedHelper;
export const getCurrentUser = getCurrentUserHelper;
export const getCurrentAspirant = getCurrentAspirantHelper;
export const getCurrentCitizen = getCurrentCitizenHelper;
export const getToken = getTokenHelper;
export const handleApiError = handleApiErrorHelper;
export const clearAuth = clearAuthHelper;

// ============ GENERIC API CALL FUNCTION ============

export const makeApiCall = async (
  url: string,
  options: RequestInit = {},
  requireAuth: boolean = true
) => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (typeof options.headers === 'object') {
        Object.entries(options.headers as Record<string, string>).forEach(([key, value]) => {
          headers[key] = value;
        });
      }
    }
    
    if (requireAuth) {
      const token = getTokenHelper();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }
    
    const fullUrl = url.startsWith('/') ? `${API_BASE_URL}${url}` : `${API_BASE_URL}/${url}`;
    const response = await fetch(fullUrl, {
      ...options,
      headers,
    });
    
    if (!response.ok) {
      if (response.status === 401) {
        clearAuthHelper();
        throw new Error('Session expired');
      }
      
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('API call failed:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

// Add global debug functions
if (typeof window !== 'undefined') {
  (window as any).debugApi = function() {
    console.log('=== API DEBUG ===');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Token:', localStorage.getItem('token') ? '✅ Present' : '❌ Missing');
    
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Token payload:', payload);
        console.log('User ID:', payload.id);
        console.log('User role:', payload.role);
      } catch (e) {
        console.log('❌ Invalid token format');
      }
    }
    
    console.log('User:', JSON.parse(localStorage.getItem('user') || 'null'));
    console.log('Aspirant:', JSON.parse(localStorage.getItem('aspirant') || 'null'));
    console.log('Citizen:', JSON.parse(localStorage.getItem('citizen') || 'null'));
    console.log('getAspirantId():', getAspirantId());
    console.log('getCitizenId():', getCitizenIdHelper());
  };
}

// ============ DEFAULT EXPORT ============

export default api;